/**
 * Typed API client for network-ia-api.
 * Token stored in localStorage; attached to every request.
 */
import i18n from '@/i18n';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4007';

const TOKEN_KEY = 'network_ia_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** Lot 1d (2026-10-28) — espace courant (identifiant du propriétaire), envoyé en X-Workspace sur chaque requête. */
export const WORKSPACE_KEY = 'nia-workspace';
export function getWorkspaceId(): string | null {
  try { return localStorage.getItem(WORKSPACE_KEY); } catch { return null; }
}
export function setWorkspaceId(id: string | null): void {
  try { if (id) localStorage.setItem(WORKSPACE_KEY, id); else localStorage.removeItem(WORKSPACE_KEY); } catch { /* stockage indisponible */ }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  auth?: boolean;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true } = opts;
  const headers: Record<string, string> = { "Content-Type": "application/json", "X-Lang": (localStorage.getItem("nia-lang") ?? "fr").slice(0, 2) };
  const workspace = getWorkspaceId();
  if (workspace) headers["X-Workspace"] = workspace; // lot 1d — routes acteur : ignoré par le serveur
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  } catch {
    // Lot 8 (2026-10-29) — vraie panne réseau (serveur injoignable, hors ligne) : le seul cas où « Erreur réseau » est juste.
    throw new ApiError(0, 'network_error', i18n.t('common.networkError'));
  }
  // 204 No Content (e.g. DELETE) — nothing to parse.
  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = data as { error?: string; message?: string };
    // Un refus sans texte n'est pas une panne réseau : erreur générique avec son code, pour qu'on sache quoi chercher.
    throw new ApiError(res.status, err.error ?? 'error', err.message ?? `${i18n.t('common.errorGeneric')} (${err.error ?? res.status})`);
  }
  return data as T;
}

/**
 * Lot 1 (2026-09-03) — 429 renvoyé par quotaManager quand le plafond IA du
 * mois est atteint. À afficher comme une information, pas comme une erreur.
 */
export function isQuotaError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 429 && err.code === 'API_BUDGET_EXCEEDED';
}
